import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, TableShell, Loading, EmptyState } from "@/components/common";
import UploadDialog from "@/components/modals/UploadDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { gbp, num, formatDate } from "@/lib/format";
import { Plus, UploadCloud, Search, Pencil, Trash2, Download } from "lucide-react";

const BLANK = { period: "", site: "", cust_group: "", product_description: "", product_cat: "", invoices: 0, quantity: 0, cogs: 0, picks: 0 };

export default function CostOfGoodsTable() {
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery({ queryKey: ["cost-of-goods"], queryFn: () => api.get("/cost-of-goods").then((r) => r.data) });
  const { data: periodMap = {} } = useQuery({ queryKey: ["period-map"], queryFn: () => api.get("/period-map").then((r) => r.data) });
  const monthOf = (period) => periodMap[period] || period;

  const [search, setSearch] = useState("");
  const [monthFilter, setMonthFilter] = useState("all");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const monthsList = useMemo(() => [...new Set(data.map((d) => monthOf(d.period)).filter(Boolean))], [data, periodMap]);

  const filtered = useMemo(() => data.filter((r) => {
    if (monthFilter !== "all" && monthOf(r.period) !== monthFilter) return false;
    if (search) {
      const s = search.toLowerCase();
      if (!`${r.product_description} ${r.cust_group} ${r.product_cat}`.toLowerCase().includes(s)) return false;
    }
    return true;
  }), [data, monthFilter, search, periodMap]);

  const totalCogs = filtered.reduce((a, b) => a + (b.cogs || 0), 0);

  const openAdd = () => { setEditing(null); setForm(BLANK); setDialogOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ period: r.period, site: r.site || "", cust_group: r.cust_group, product_description: r.product_description, product_cat: r.product_cat, invoices: r.invoices, quantity: r.quantity, cogs: r.cogs, picks: r.picks });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.period.trim()) { toast.error("Period is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, invoices: Number(form.invoices) || 0, quantity: Number(form.quantity) || 0, cogs: Number(form.cogs) || 0, picks: Number(form.picks) || 0 };
      if (editing) await api.put(`/cost-of-goods/${editing.id}`, payload);
      else await api.post("/cost-of-goods", payload);
      toast.success(editing ? "Record updated" : "Record added");
      qc.invalidateQueries({ queryKey: ["cost-of-goods"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      setDialogOpen(false);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/cost-of-goods/${deleteTarget.id}`);
      toast.success("Record deleted");
      qc.invalidateQueries({ queryKey: ["cost-of-goods"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setDeleteTarget(null); }
  };

  return (
    <div>
      <PageHeader title="Cost of Goods" subtitle="Manual invoice and cost-of-goods records by period. Upload to keep them current.">
        <Button variant="outline" onClick={() => setUploadOpen(true)} data-testid="cogs-upload-invoice-button"><UploadCloud className="w-4 h-4 mr-1.5" /> Upload</Button>
        <Button onClick={openAdd} className="bg-[#d4333b] hover:bg-[#b82a31]" data-testid="cogs-add-button"><Plus className="w-4 h-4 mr-1.5" /> Add record</Button>
      </PageHeader>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search product, group or category…" className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="cogs-search-input" />
        </div>
        <Select value={monthFilter} onValueChange={setMonthFilter}>
          <SelectTrigger className="w-full sm:w-52" data-testid="cogs-status-filter"><SelectValue placeholder="Month" /></SelectTrigger>
          <SelectContent className="max-h-64">
            <SelectItem value="all">All months</SelectItem>
            {monthsList.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? <Loading /> : (
        <TableShell>
          <table className="w-full text-sm" data-testid="cogs-invoices-table">
            <thead>
              <tr className="bg-slate-100 dark:bg-slate-800/80 text-left text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400">
                <th className="px-4 py-3">Month</th>
                <th className="px-4 py-3">Site</th>
                <th className="px-4 py-3">Cust Group</th>
                <th className="px-4 py-3">Product</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3 text-right">Invoices</th>
                <th className="px-4 py-3 text-right">Quantity</th>
                <th className="px-4 py-3 text-right">COGS</th>
                <th className="px-4 py-3 text-right">Picks</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {filtered.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40" data-testid={`cogs-row-${r.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-800 dark:text-slate-200">{monthOf(r.period)}</td>
                  <td className="px-4 py-3 text-slate-600">{r.site || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{r.cust_group || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{r.product_description || "—"}</td>
                  <td className="px-4 py-3 text-slate-500">{r.product_cat || "—"}</td>
                  <td className="px-4 py-3 text-right font-mono-num">{num(r.invoices)}</td>
                  <td className="px-4 py-3 text-right font-mono-num">{num(r.quantity)}</td>
                  <td className="px-4 py-3 text-right font-mono-num">{gbp(r.cogs)}</td>
                  <td className="px-4 py-3 text-right font-mono-num">{num(r.picks)}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(r)} data-testid={`cogs-edit-${r.id}`}><Pencil className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500 hover:text-red-600" onClick={() => setDeleteTarget(r)} data-testid={`cogs-delete-${r.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            {filtered.length > 0 && (
              <tfoot>
                <tr className="bg-slate-50 dark:bg-slate-800/60 font-semibold">
                  <td className="px-4 py-3" colSpan={7}>Total COGS ({filtered.length} records)</td>
                  <td className="px-4 py-3 text-right font-mono-num">{gbp(totalCogs)}</td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            )}
          </table>
          {filtered.length === 0 && <EmptyState message="No cost-of-goods records match your filters." />}
        </TableShell>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="cogs-form-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit record" : "Add record"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><Label>Period *</Label><Input className="mt-1.5" value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} placeholder="PERIOD 0127" data-testid="cogs-form-period" /></div>
            <div><Label>Site</Label><Input className="mt-1.5" value={form.site} onChange={(e) => setForm({ ...form, site: e.target.value })} placeholder="Greenside" /></div>
            <div><Label>Customer Group</Label><Input className="mt-1.5" value={form.cust_group} onChange={(e) => setForm({ ...form, cust_group: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label>Product Description</Label><Input className="mt-1.5" value={form.product_description} onChange={(e) => setForm({ ...form, product_description: e.target.value })} /></div>
            <div><Label>Category</Label><Input className="mt-1.5" value={form.product_cat} onChange={(e) => setForm({ ...form, product_cat: e.target.value })} /></div>
            <div><Label>Invoices</Label><Input className="mt-1.5" type="number" value={form.invoices} onChange={(e) => setForm({ ...form, invoices: e.target.value })} /></div>
            <div><Label>Quantity</Label><Input className="mt-1.5" type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} /></div>
            <div><Label>COGS (£)</Label><Input className="mt-1.5" type="number" step="0.01" value={form.cogs} onChange={(e) => setForm({ ...form, cogs: e.target.value })} /></div>
            <div><Label>Picks</Label><Input className="mt-1.5" type="number" value={form.picks} onChange={(e) => setForm({ ...form, picks: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#d4333b] hover:bg-[#b82a31]" data-testid="cogs-save-button">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        entity="cost-of-goods"
        columns={["period", "cust_group", "product_description", "product_cat", "invoices", "quantity", "cogs", "picks"]}
        onDone={() => { qc.invalidateQueries({ queryKey: ["cost-of-goods"] }); qc.invalidateQueries({ queryKey: ["stats"] }); }}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete record?</AlertDialogTitle>
            <AlertDialogDescription>This will permanently remove this cost-of-goods record.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
